import {
  Box,
  List,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  BoxMotion,
  TypographyMotion,
} from "components/elements/material_motion";
import ProjectSectionFrame from "components/elements/project_section_frame";
import FrameGlassCard from "components/views/frame_glass_card";
import { firebaseDatabase } from "config/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { FrameGlassType } from "utils/other/enums";
import { NAVIGATION_SET_MENU } from "utils/redux/action";
import ProjectDesktopPreview from "components/elements/project_desktop_preview";
import ProjectPhonePreview from "components/elements/project_phone_preview";

export default function ProjectDetailPage() {
  const theme = useTheme();
  const matchUpSm = useMediaQuery(theme.breakpoints.up("sm"));
  const matchUpMd = useMediaQuery(theme.breakpoints.up("md"));

  const dispatcher = useDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);

  useEffect(() => {
    dispatcher({ type: NAVIGATION_SET_MENU, index: 3 });
  }, [dispatcher]);

  useEffect(() => {
    const listenerProject = onSnapshot(
      doc(firebaseDatabase, "projects", params.id),
      (snapshot) => {
        if (snapshot.exists()) {
          setProject({
            id: snapshot.id,
            ...snapshot.data(),
          });
        } else {
          navigate("/error");
        }
      }
    );

    return () => {
      listenerProject();
    };
  }, [navigate, params]);

  return project === null ? (
    <Fragment />
  ) : (
    <Box sx={{ position: "relative", padding: { xs: 0, sm: "0 10px" } }}>
      <TypographyMotion
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", delay: 0.0, duration: 1 }}
        variant="h2"
        sx={{
          fontFamily: "Poppins",
          fontWeight: 700,
          fontSize: { xs: 20, md: 24 },
          color: "black",
          wordWrap: "break-word",
          margin: 0,
        }}
      >
        {project.name}
      </TypographyMotion>
      <Box height={20} />
      <BoxMotion
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ type: "spring", delay: 0.0, duration: 1 }}
      >
        <FrameGlassCard
          type={FrameGlassType.Doff}
          borderRadius={matchUpSm ? "50px" : "20px"}
          outline="2px solid rgba(255, 255, 255, 0.30);"
        >
          <Box
            sx={{
              aspectRatio: matchUpSm ? (matchUpMd ? 16 / 7 : 16 / 10) : 16 / 16,
              padding: 5,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            {project.platform.toLowerCase().includes("web") ? (
              <ProjectDesktopPreview url={project.preview} />
            ) : (
              <ProjectPhonePreview url={project.preview} />
            )}
          </Box>
        </FrameGlassCard>
      </BoxMotion>
      <Box height={30} />
      <BoxMotion
        initial={{ x: -30, opacity: 0 }}
        animate={{ x: -0, opacity: 1 }}
        transition={{ type: "spring", delay: 0.2, duration: 1 }}
      >
        <ProjectSectionFrame>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: { xs: 16, md: 18 },
              textShadow: "0px 4px 4px rgba(255, 255, 255, 0.25)",
            }}
          >
            Deskripsi
          </Typography>
        </ProjectSectionFrame>
      </BoxMotion>
      <Box height={10} />
      <BoxMotion
        initial={{ x: -30, opacity: 0 }}
        animate={{ x: -0, opacity: 1 }}
        transition={{ type: "spring", delay: 0.2, duration: 1 }}
      >
        <ProjectSectionFrame fullwidth>
          <Typography
            variant="p"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 400,
              fontSize: 14,
              textShadow: "0px 4px 4px rgba(255, 255, 255, 0.25)",
              textAlign: { xs: "start", sm: "justify" },
              whiteSpace: "pre-wrap",
              display: "inline-block",
            }}
          >
            {project.description
              ?.replaceAll("\\t", matchUpSm ? "\u0009" : "")
              .replaceAll("\\n", "\u000A")}
          </Typography>
        </ProjectSectionFrame>
      </BoxMotion>
      <Box height={30} />
      <BoxMotion
        initial={{ x: -30, opacity: 0 }}
        animate={{ x: -0, opacity: 1 }}
        transition={{ type: "spring", delay: 0.2, duration: 1 }}
      >
        <ProjectSectionFrame>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: { xs: 16, md: 18 },
              textShadow: "0px 4px 4px rgba(255, 255, 255, 0.25)",
            }}
          >
            Fitur Tersedia
          </Typography>
        </ProjectSectionFrame>
      </BoxMotion>
      <Box height={10} />
      <BoxMotion
        initial={{ x: -30, opacity: 0 }}
        animate={{ x: -0, opacity: 1 }}
        transition={{ type: "spring", delay: 0.2, duration: 1 }}
      >
        <ProjectSectionFrame fullwidth>
          <List
            component="ul"
            sx={{
              listStyleType: "disc",
              padding: "0 0 0 10px",
              margin: 0,
            }}
          >
            {project.features?.map((_, __) => (
              <ListItemText
                key={__}
                primary={_}
                primaryTypographyProps={{
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  fontSize: 14,
                  color: "black",
                  wordWrap: "break-word",
                }}
                sx={{
                  display: "list-item",
                }}
              />
            ))}
          </List>
        </ProjectSectionFrame>
      </BoxMotion>
      <Box height={30} />
      <BoxMotion
        initial={{ x: -30, opacity: 0 }}
        animate={{ x: -0, opacity: 1 }}
        transition={{ type: "spring", delay: 0.2, duration: 1 }}
      >
        <ProjectSectionFrame>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: { xs: 16, md: 18 },
              textShadow: "0px 4px 4px rgba(255, 255, 255, 0.25)",
            }}
          >
            Teknologi Yang Digunakan
          </Typography>
        </ProjectSectionFrame>
      </BoxMotion>
      <Box height={10} />
      <BoxMotion
        initial={{ x: -30, opacity: 0 }}
        animate={{ x: -0, opacity: 1 }}
        transition={{ type: "spring", delay: 0.2, duration: 1 }}
      >
        <ProjectSectionFrame fullwidth>
          <List
            component="ul"
            sx={{
              listStyleType: "disc",
              padding: "0 0 0 10px",
              margin: 0,
            }}
          >
            {project.technologies?.map((_, __) => (
              <ListItemText
                key={__}
                primary={_}
                primaryTypographyProps={{
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  fontSize: 14,
                  color: "black",
                  wordWrap: "break-word",
                }}
                sx={{
                  display: "list-item",
                }}
              />
            ))}
          </List>
        </ProjectSectionFrame>
      </BoxMotion>
      {project.url ? (
        <Fragment>
          <Box height={30} />
          <BoxMotion
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: -0, opacity: 1 }}
            transition={{ type: "spring", delay: 0.2, duration: 1 }}
          >
            <ProjectSectionFrame>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  fontSize: { xs: 16, md: 18 },
                  textShadow: "0px 4px 4px rgba(255, 255, 255, 0.25)",
                }}
              >
                Referensi Projek
              </Typography>
            </ProjectSectionFrame>
          </BoxMotion>
          <Box height={10} />
          <BoxMotion
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: -0, opacity: 1 }}
            transition={{ type: "spring", delay: 0.2, duration: 1 }}
          >
            <ProjectSectionFrame fullwidth>
              <Link
                to={project.url}
                target="_blank"
                style={{ textDecoration: "none" }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: 14,
                    textShadow: "0px 4px 4px rgba(255, 255, 255, 0.25)",
                    wordWrap: "break-word",
                  }}
                >
                  {project.url}
                </Typography>
              </Link>
            </ProjectSectionFrame>
          </BoxMotion>
        </Fragment>
      ) : (
        <Fragment />
      )}
    </Box>
  );
}
